/**
 * Loan Lifecycle DTOs (Issue #1388 — strict isolation from Pipeline)
 *
 * Per-business loan stage configuration: stages, substages, transitions,
 * data points, and stage requirements. The Loan Lifecycle subsystem is
 * deliberately separated from the generic Pipeline (which still serves
 * tickets / feedback / leads). These DTOs are the wire format used by
 * gRPC RPCs in 29-case-management-service and tRPC routers in 2-api-gateway.
 *
 * Strict isolation: nothing in this file references PipelineStage,
 * PipelineId, or any pipeline-domain DTO. Loans never read from or
 * write to Pipeline tables.
 */

import { z } from 'zod';
import { LoanStageCapability } from '../enums/loan-stage-capability.enums';
import { applicabilityRuleSchema } from '../utils/applicability-rule';

// ============================================================================
// SHARED VALIDATION HELPERS
// ============================================================================

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

const CapabilitySchema = z.union([z.nativeEnum(LoanStageCapability), z.string().min(1).max(64)]);

const RequirementLevelEnum = z.enum(['REQUIRED', 'OPTIONAL']);
export const LoanRequirementLevel = {
  REQUIRED: 'REQUIRED',
  OPTIONAL: 'OPTIONAL',
} as const;
export type LoanRequirementLevel = (typeof LoanRequirementLevel)[keyof typeof LoanRequirementLevel];

const DataPointCategoryEnum = z.string().min(1).max(64);
export const LOAN_DATA_POINT_CATEGORIES = ['BORROWER', 'PROPERTY', 'FINANCIAL', 'LEGAL', 'EMPLOYMENT'] as const;

const DataPointValueTypeEnum = z.enum(['STRING', 'NUMBER', 'DATE', 'BOOLEAN', 'CURRENCY', 'DOCUMENT']);
export const LoanDataPointValueType = {
  STRING: 'STRING',
  NUMBER: 'NUMBER',
  DATE: 'DATE',
  BOOLEAN: 'BOOLEAN',
  CURRENCY: 'CURRENCY',
  /**
   * Issue #1480 Phase E — represents an attachment-backed data point
   * (DOCUMENT_SLOT). Matches the canonical loan-data-point value-type
   * column in `29-case-management-service` schema.prisma.
   */
  DOCUMENT: 'DOCUMENT',
} as const;
export type LoanDataPointValueType = (typeof LoanDataPointValueType)[keyof typeof LoanDataPointValueType];

// ============================================================================
// LoanProduct DTO
// ============================================================================

export const LoanProductRowSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullable(),
  category: z.string().max(64).nullable(),
  icon: z.string().max(64).nullable(),
  color: z.string().regex(HEX_COLOR).nullable(),
  defaultIntakeTemplateId: z.string().uuid().nullable(),
  isActive: z.boolean(),
  isSystem: z.boolean(),
  sortOrder: z.number().int().nonnegative(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.union([z.string(), z.date()]).optional(),
  updatedAt: z.union([z.string(), z.date()]).optional(),
});
export type LoanProductDto = z.infer<typeof LoanProductRowSchema>;

export const CreateLoanProductInputSchema = LoanProductRowSchema.omit({
  id: true,
  businessId: true,
  isSystem: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  description: z.string().max(500).nullable().optional(),
  category: z.string().max(64).nullable().optional(),
  icon: z.string().max(64).nullable().optional(),
  color: z.string().regex(HEX_COLOR).nullable().optional(),
  defaultIntakeTemplateId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().nonnegative().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type CreateLoanProductInput = z.infer<typeof CreateLoanProductInputSchema>;

export const UpdateLoanProductInputSchema = CreateLoanProductInputSchema.partial();
export type UpdateLoanProductInput = z.infer<typeof UpdateLoanProductInputSchema>;

// ============================================================================
// LoanStage DTO
// ============================================================================

export const LoanStageRowSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  loanProductId: z.string().uuid().nullable(),
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullable(),
  icon: z.string().max(64).nullable(),
  color: z.string().regex(HEX_COLOR),
  order: z.number().int().nonnegative(),
  capabilities: z.array(CapabilitySchema).default([]),
  isInitial: z.boolean(),
  isTerminal: z.boolean(),
  slaHours: z.number().int().positive().nullable(),
  pauseSla: z.boolean(),
  terminalReason: z.string().max(64).nullable(),
  isActive: z.boolean(),
  isSystem: z.boolean(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.union([z.string(), z.date()]).optional(),
  updatedAt: z.union([z.string(), z.date()]).optional(),
});
export type LoanStageDto = z.infer<typeof LoanStageRowSchema>;

export const CreateLoanStageInputSchema = LoanStageRowSchema.omit({
  id: true,
  businessId: true,
  isInitial: true,
  isTerminal: true,
  isSystem: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  loanProductId: z.string().uuid().nullable().optional(),
  description: z.string().max(500).nullable().optional(),
  icon: z.string().max(64).nullable().optional(),
  slaHours: z.number().int().positive().nullable().optional(),
  pauseSla: z.boolean().optional(),
  terminalReason: z.string().max(64).nullable().optional(),
  isActive: z.boolean().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  order: z.number().int().nonnegative().optional(),
});
export type CreateLoanStageInput = z.infer<typeof CreateLoanStageInputSchema>;

export const UpdateLoanStageInputSchema = CreateLoanStageInputSchema.partial();
export type UpdateLoanStageInput = z.infer<typeof UpdateLoanStageInputSchema>;

export const ReorderLoanStagesInputSchema = z.object({
  loanProductId: z.string().uuid().nullable().optional(),
  orderedStageIds: z.array(z.string().uuid()).min(1),
});
export type ReorderLoanStagesInput = z.infer<typeof ReorderLoanStagesInputSchema>;

export const ForkStagesForProductInputSchema = z.object({
  loanProductId: z.string().uuid(),
});
export type ForkStagesForProductInput = z.infer<typeof ForkStagesForProductInputSchema>;

// ============================================================================
// LoanSubstage DTO
// ============================================================================

export const LoanSubstageRowSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  loanStageId: z.string().uuid(),
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullable(),
  order: z.number().int().nonnegative(),
  capabilities: z.array(CapabilitySchema).default([]),
  isActive: z.boolean(),
  isSystem: z.boolean(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.union([z.string(), z.date()]).optional(),
  updatedAt: z.union([z.string(), z.date()]).optional(),
});
export type LoanSubstageDto = z.infer<typeof LoanSubstageRowSchema>;

export const CreateLoanSubstageInputSchema = LoanSubstageRowSchema.omit({
  id: true,
  businessId: true,
  isSystem: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  description: z.string().max(500).nullable().optional(),
  isActive: z.boolean().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  order: z.number().int().nonnegative().optional(),
});
export type CreateLoanSubstageInput = z.infer<typeof CreateLoanSubstageInputSchema>;

export const UpdateLoanSubstageInputSchema = CreateLoanSubstageInputSchema.partial();
export type UpdateLoanSubstageInput = z.infer<typeof UpdateLoanSubstageInputSchema>;

export const ReorderLoanSubstagesInputSchema = z.object({
  loanStageId: z.string().uuid(),
  orderedSubstageIds: z.array(z.string().uuid()).min(1),
});
export type ReorderLoanSubstagesInput = z.infer<typeof ReorderLoanSubstagesInputSchema>;

// ============================================================================
// LoanStageTransition DTO
// ============================================================================

export const LoanStageTransitionSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  fromStageId: z.string().uuid(),
  toStageId: z.string().uuid(),
  requireRole: z.string().max(64).nullable(),
  requireApproval: z.boolean(),
  isBackward: z.boolean(),
  createdAt: z.union([z.string(), z.date()]).optional(),
});
export type LoanStageTransitionDto = z.infer<typeof LoanStageTransitionSchema>;

export const CreateLoanStageTransitionInputSchema = LoanStageTransitionSchema.omit({
  id: true,
  businessId: true,
  createdAt: true,
}).extend({
  requireRole: z.string().max(64).nullable().optional(),
  requireApproval: z.boolean().optional(),
  isBackward: z.boolean().optional(),
});
export type CreateLoanStageTransitionInput = z.infer<typeof CreateLoanStageTransitionInputSchema>;

export const ValidateLoanStageGraphInputSchema = z.object({
  loanProductId: z.string().uuid().nullable().optional(),
});
export type ValidateLoanStageGraphInput = z.infer<typeof ValidateLoanStageGraphInputSchema>;

export const LoanStageGraphValidationSchema = z.object({
  isValid: z.boolean(),
  issues: z.array(
    z.object({
      severity: z.enum(['warning', 'error']),
      code: z.enum([
        'NO_INITIAL_STAGE',
        'MULTIPLE_INITIAL_STAGES',
        'NO_TERMINAL_STAGE',
        'STRANDED_STAGE',
        'UNREACHABLE_STAGE',
        'CYCLE_DETECTED',
      ]),
      stageId: z.string().uuid().optional(),
      stageName: z.string().optional(),
      message: z.string(),
    }),
  ),
});
export type LoanStageGraphValidation = z.infer<typeof LoanStageGraphValidationSchema>;

// ============================================================================
// LoanDataPoint DTO
// ============================================================================

/**
 * Issue #1480 — distinguishes data fields satisfied by a Case column /
 * suggestion (DATA_POINT) from closing-checklist document slots fulfilled
 * by an uploaded attachment (DOCUMENT_SLOT). Defaults to DATA_POINT for
 * backward compatibility with the 33 system data points seeded by
 * PR #1402.
 */
export const LoanDataPointKindEnum = z.enum(['DATA_POINT', 'DOCUMENT_SLOT']);
export type LoanDataPointKind = z.infer<typeof LoanDataPointKindEnum>;

export const LoanDataPointSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  canonicalKey: z.string().min(1).max(120),
  displayName: z.string().min(1).max(120),
  category: DataPointCategoryEnum,
  valueType: DataPointValueTypeEnum,
  kind: LoanDataPointKindEnum.default('DATA_POINT'),
  extractableFrom: z.array(z.string()).default([]),
  survivorshipRule: z.string().max(64).nullable(),
  validation: z.record(z.string(), z.unknown()).nullable(),
  caseFieldName: z.string().max(120).nullable(),
  isActive: z.boolean(),
  isSystem: z.boolean(),
  createdAt: z.union([z.string(), z.date()]).optional(),
  updatedAt: z.union([z.string(), z.date()]).optional(),
});
export type LoanDataPointDto = z.infer<typeof LoanDataPointSchema>;

export const CreateLoanDataPointInputSchema = LoanDataPointSchema.omit({
  id: true,
  businessId: true,
  isSystem: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  survivorshipRule: z.string().max(64).nullable().optional(),
  validation: z.record(z.string(), z.unknown()).nullable().optional(),
  caseFieldName: z.string().max(120).nullable().optional(),
  isActive: z.boolean().optional(),
  extractableFrom: z.array(z.string()).optional(),
  kind: LoanDataPointKindEnum.optional(),
});
export type CreateLoanDataPointInput = z.infer<typeof CreateLoanDataPointInputSchema>;

export const UpdateLoanDataPointInputSchema = CreateLoanDataPointInputSchema.partial();
export type UpdateLoanDataPointInput = z.infer<typeof UpdateLoanDataPointInputSchema>;

// ============================================================================
// LoanStageRequirement DTO
// ============================================================================

export const LoanStageRequirementSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  loanStageId: z.string().uuid(),
  loanSubstageId: z.string().uuid().nullable(),
  loanDataPointId: z.string().uuid(),
  loanProductId: z.string().uuid().nullable(),
  requirement: RequirementLevelEnum,
  blocksTransition: z.boolean(),
  createdAt: z.union([z.string(), z.date()]).optional(),
  updatedAt: z.union([z.string(), z.date()]).optional(),
});
export type LoanStageRequirementDto = z.infer<typeof LoanStageRequirementSchema>;

export const SetLoanStageRequirementsInputSchema = z.object({
  loanStageId: z.string().uuid(),
  loanProductId: z.string().uuid().nullable().optional(),
  requirements: z.array(
    z.object({
      loanDataPointId: z.string().uuid(),
      loanSubstageId: z.string().uuid().nullable().optional(),
      requirement: RequirementLevelEnum,
      blocksTransition: z.boolean().optional(),
    }),
  ),
});
export type SetLoanStageRequirementsInput = z.infer<typeof SetLoanStageRequirementsInputSchema>;

export const CheckStageExitInputSchema = z.object({
  caseId: z.string().uuid(),
  targetStageId: z.string().uuid(),
});
export type CheckStageExitInput = z.infer<typeof CheckStageExitInputSchema>;

export const StageExitMissingItemSchema = z.object({
  loanDataPointId: z.string().uuid(),
  canonicalKey: z.string(),
  displayName: z.string(),
  category: z.string(),
  requirement: RequirementLevelEnum,
  /**
   * Issue #1480 — distinguishes data fields satisfied by a Case column /
   * suggestion (DATA_POINT) from closing-checklist document slots fulfilled
   * by an uploaded attachment (DOCUMENT_SLOT). UI uses this to choose between
   * a "Fill in" affordance and a "Upload doc" affordance.
   */
  kind: LoanDataPointKindEnum.default('DATA_POINT'),
  /** Human-readable rationale for conditional requirements. */
  applicabilityNote: z.string().nullable().optional(),
});
export type StageExitMissingItem = z.infer<typeof StageExitMissingItemSchema>;

export const StageExitCheckResultSchema = z.object({
  canExit: z.boolean(),
  targetStageId: z.string().uuid(),
  targetStageName: z.string(),
  missingRequired: z.array(StageExitMissingItemSchema),
});
export type StageExitCheckResult = z.infer<typeof StageExitCheckResultSchema>;

// ============================================================================
// CaseEntryRequirements — next-stage blocker surface for LoanCaseStageIndicator
// ============================================================================

/**
 * Input for `loanStages.getCaseEntryRequirements`. If `targetStageId` is
 * omitted, the service resolves the case's current stage and returns the
 * blockers for the next non-terminal stage in order. The frontend's stage
 * indicator popover only ever asks for "what's missing to advance" so the
 * common case omits `targetStageId`.
 */
export const GetCaseEntryRequirementsInputSchema = z.object({
  caseId: z.string().uuid(),
  targetStageId: z.string().uuid().optional(),
});
export type GetCaseEntryRequirementsInput = z.infer<typeof GetCaseEntryRequirementsInputSchema>;

/**
 * A single unmet REQUIRED data point that blocks the case from advancing
 * to the target stage. `category` is uppercase (BORROWER / PROPERTY /
 * FINANCIAL / LEGAL / EMPLOYMENT / closing.* / custom) so the popover can
 * group blockers without further coercion. `navigationHint` is an optional
 * deep-link path the frontend can use to jump straight to the field's editor.
 */
export const CaseEntryRequirementBlockerSchema = z.object({
  dataPointKey: z.string(),
  displayName: z.string(),
  category: z.string(),
  navigationHint: z.string().nullable().optional(),
});
export type CaseEntryRequirementBlocker = z.infer<typeof CaseEntryRequirementBlockerSchema>;

export const CaseEntryRequirementsResultSchema = z.object({
  targetStageId: z.string().uuid(),
  targetStageName: z.string(),
  totalRequired: z.number().int().nonnegative(),
  metCount: z.number().int().nonnegative(),
  blockers: z.array(CaseEntryRequirementBlockerSchema),
});
export type CaseEntryRequirementsResult = z.infer<typeof CaseEntryRequirementsResultSchema>;
export type CaseEntryRequirementsDto = CaseEntryRequirementsResult;

// ============================================================================
// Issue #1480 Phase E — Closing Checklist For Case
// ============================================================================

/**
 * Per-case override entry for one closing-checklist item, keyed by
 * `loanStageRequirementId` in `Case.closingChecklistState` (Json column).
 * Captures only the *override* of the template — completed/notes/promote.
 *
 * Invariant (enforced via `superRefine`): when `overriddenRequired === true`,
 * `overrideReason`, `overriddenAt`, and `overriddenBy` are all REQUIRED.
 * This prevents the per-case state from persisting a half-formed override
 * that the audit log can't reconstruct.
 */
export const ClosingChecklistOverrideSchema = z
  .object({
    /** ISO timestamp the item was marked complete. */
    completedAt: z.string().datetime().nullable().optional(),
    /** User id (UUID) who marked the item complete. */
    completedBy: z.string().uuid().nullable().optional(),
    /** Lender-only notes, free-form. */
    notes: z.string().max(2000).nullable().optional(),
    /** Promote an OPTIONAL template row to REQUIRED for this single case. */
    overriddenRequired: z.boolean().nullable().optional(),
    /** Required when `overriddenRequired === true` — short rationale. */
    overrideReason: z.string().max(280).nullable().optional(),
    /** ISO timestamp of the override action. Required when `overriddenRequired === true`. */
    overriddenAt: z.string().datetime().nullable().optional(),
    /** User id (UUID) who applied the override. Required when `overriddenRequired === true`. */
    overriddenBy: z.string().uuid().nullable().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.overriddenRequired !== true) {
      return;
    }

    if (typeof value.overrideReason !== 'string' || value.overrideReason.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['overrideReason'],
        message: 'overrideReason is required when overriddenRequired is true',
      });
    }

    if (value.overriddenAt == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['overriddenAt'],
        message: 'overriddenAt is required when overriddenRequired is true',
      });
    }

    if (value.overriddenBy == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['overriddenBy'],
        message: 'overriddenBy is required when overriddenRequired is true',
      });
    }
  });
export type ClosingChecklistOverride = z.infer<typeof ClosingChecklistOverrideSchema>;

/**
 * One row in the rich closing-checklist response. Combines:
 *  - the template (loanStageRequirementId + dataPoint + applicabilityRule/Note)
 *  - the live applicability evaluation (does the rule currently match?)
 *  - the per-case override state (completed/notes/promote)
 *  - the fulfillment state (is there a FINAL attachment? is the column set?)
 *
 * The UI groups rows into two buckets:
 *  - REQUIRED (always)              — `requirement === 'REQUIRED'` AND
 *                                     (no rule OR rule matches)
 *  - IF-APPLICABLE (collapsed)      — `requirement === 'OPTIONAL'` OR rule didn't
 *                                     match (or REQUIRED with rule that didn't match)
 */
export const ClosingChecklistItemSchema = z.object({
  /** Stable id of the LoanStageRequirement template row. */
  loanStageRequirementId: z.string().uuid(),
  loanDataPointId: z.string().uuid(),
  canonicalKey: z.string(),
  displayName: z.string(),
  category: z.string(),
  /** Per-bucket sorting/grouping — UI typically renders all DOCUMENT_SLOT rows under "Documents". */
  kind: LoanDataPointKindEnum,
  /**
   * Reuses the canonical `LoanDataPointValueType` enum so closing-checklist
   * value types stay aligned with the rest of the loan-lifecycle surface
   * (STRING | NUMBER | DATE | BOOLEAN | CURRENCY | DOCUMENT). Nullable for
   * forward-compat with rows whose value type wasn't set on the template.
   */
  valueType: DataPointValueTypeEnum.nullable(),
  /** REQUIRED | OPTIONAL — base template requirement. */
  requirement: RequirementLevelEnum,
  /** Does this requirement currently apply (after evaluating the rule)? */
  applies: z.boolean(),
  /** The validated rule (already JSON-decoded). Null = always applies. */
  applicabilityRule: applicabilityRuleSchema.nullable(),
  /** Human-readable rationale for the rule, surfaced in tooltips. */
  applicabilityNote: z.string().nullable(),
  /** Has an attachment been uploaded into this slot? (Only meaningful for DOCUMENT_SLOT.) */
  documentUploaded: z.boolean(),
  /** True if the underlying data is satisfied (Case column / metadata / suggestion / attachment). */
  fulfilled: z.boolean(),
  /** Per-case override state — null when no override has ever been written. */
  override: ClosingChecklistOverrideSchema.nullable(),
});
export type ClosingChecklistItem = z.infer<typeof ClosingChecklistItemSchema>;

export const ClosingChecklistForCaseSchema = z.object({
  caseId: z.string().uuid(),
  loanProductId: z.string().uuid().nullable(),
  loanStageId: z.string().uuid().nullable(),
  /** Target close date pulled from Case.metadata.closing.targetDate. */
  targetCloseDate: z.string().nullable(),
  items: z.array(ClosingChecklistItemSchema),
  /** Counters useful for the page header / banner. */
  totals: z.object({
    requiredTotal: z.number().int().min(0),
    requiredComplete: z.number().int().min(0),
    conditionalTotal: z.number().int().min(0),
    conditionalComplete: z.number().int().min(0),
    ifApplicableTotal: z.number().int().min(0),
  }),
});
export type ClosingChecklistForCase = z.infer<typeof ClosingChecklistForCaseSchema>;

export const GetClosingChecklistForCaseInputSchema = z.object({
  caseId: z.string().uuid(),
});
export type GetClosingChecklistForCaseInput = z.infer<typeof GetClosingChecklistForCaseInputSchema>;

export const MarkChecklistItemCompleteInputSchema = z.object({
  caseId: z.string().uuid(),
  loanStageRequirementId: z.string().uuid(),
});
export type MarkChecklistItemCompleteInput = z.infer<typeof MarkChecklistItemCompleteInputSchema>;

export const MarkChecklistItemIncompleteInputSchema = z.object({
  caseId: z.string().uuid(),
  loanStageRequirementId: z.string().uuid(),
});
export type MarkChecklistItemIncompleteInput = z.infer<typeof MarkChecklistItemIncompleteInputSchema>;

export const SetChecklistItemNotesInputSchema = z.object({
  caseId: z.string().uuid(),
  loanStageRequirementId: z.string().uuid(),
  notes: z.string().max(2000).nullable(),
});
export type SetChecklistItemNotesInput = z.infer<typeof SetChecklistItemNotesInputSchema>;

export const PromoteChecklistItemToRequiredInputSchema = z.object({
  caseId: z.string().uuid(),
  loanStageRequirementId: z.string().uuid(),
  overrideReason: z.string().min(1).max(280),
});
export type PromoteChecklistItemToRequiredInput = z.infer<typeof PromoteChecklistItemToRequiredInputSchema>;

// ============================================================================
// TransitionLoanStage (revised input — UUID instead of enum)
// ============================================================================

export const TransitionLoanStageInputSchema = z.object({
  caseId: z.string().uuid(),
  targetStageId: z.string().uuid(),
  targetSubstageId: z.string().uuid().nullable().optional(),
  note: z.string().max(2000).optional(),
  /** Skip the requirements gate. Requires admin role; audited as override. */
  override: z.boolean().optional(),
});
export type TransitionLoanStageInput = z.infer<typeof TransitionLoanStageInputSchema>;
